import React from 'react';
import { AnnotationAuthGuard, useAuth } from '@/components/auth';
import { AnnotationWorkflow } from './annotation-workflow';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Music, User, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

interface AnnotationPlatformProps {
  onComplete?: (sessionId: string, participantId: string) => void;
  onBack?: () => void;
  className?: string;
}

export function AnnotationPlatform({ onComplete, onBack, className }: AnnotationPlatformProps) {
  const { user, participantProfile, isLoadingParticipant } = useAuth();

  const handleSessionComplete = (sessionId: string, participantId: string) => {
    toast.success('Research session completed successfully!');
    onComplete?.(sessionId, participantId);
  };

  const handleError = (error: string) => {
    toast.error(`Error: ${error}`);
  };

  return (
    <div className={className}>
      <AnnotationAuthGuard>
        <div className="space-y-6">
          {/* Platform Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Music className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Music Annotation Research</h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Help us understand how people perceive AI-generated versus human-composed music
            </p>
          </div>

          {/* User Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Participant Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Signed in as:</span>
                    <Badge variant="outline">{user?.username}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Research profile:</span>
                    {isLoadingParticipant ? (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 animate-spin" />
                        <span className="text-sm">Checking...</span>
                      </div>
                    ) : participantProfile?.hasProfile ? (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span className="text-sm text-green-600">Complete</span>
                      </div>
                    ) : (
                      <span className="text-sm text-yellow-600">Needs completion</span>
                    )}
                  </div>
                </div>
                {onBack && (
                  <Button variant="outline" onClick={onBack} className="sm:gap-2">
                    <span className="sm:inline hidden">← Back</span>
                    <span className="sm:hidden">←</span>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Main Workflow */}
          <AnnotationWorkflow
            onComplete={handleSessionComplete}
            onError={handleError}
            sessionConfig={{
              totalSongs: 20,
              balanceAcrossSources: true,
              excludeAnnotatedSongs: true
            }}
          />
        </div>


      </AnnotationAuthGuard>
    </div>
  );
}

// Wrapper component that handles authentication requirements
export function AnnotationPlatformWithAuth(props: AnnotationPlatformProps) {
  return (
    <AnnotationAuthGuard>
      <AnnotationPlatform {...props} />
    </AnnotationAuthGuard>
  );
}

// Quick access component for authenticated users
export function QuickAnnotationAccess({ onStart }: { onStart: () => void }) {
  const { user, participantProfile, isLoadingParticipant } = useAuth();

  if (!user) {
    return null;
  }

  const canStart = participantProfile?.hasProfile === true;
  const needsProfile = !isLoadingParticipant && !canStart;

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Music className="h-6 w-6 text-primary" />
          Start Your Research Session
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {needsProfile ? (
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <strong>First, complete your participant profile</strong> by clicking the button below to provide some basic demographic information about yourself.
            </p>
            <p className="text-muted-foreground">
              After setup, you'll listen to random music samples (some AI-generated, some human-composed) and help us by:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
              <li>• Identifying whether each song is AI-generated or human-composed</li>
              <li>• Rating the song's aesthetic quality and emotional impact</li>
              <li>• Providing your honest perception of the music</li>
            </ul>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <strong>You're all set!</strong> Click below to begin a new annotation session where you'll listen to music samples and provide your insights.
            </p>
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="font-medium mb-1">What to expect:</p>
              <ul className="space-y-1">
                <li>• 15-20 minute sessions with multiple music samples</li>
                <li>• Mix of AI-generated and human-composed tracks</li>
                <li>• Rate authenticity, quality, and emotional engagement</li>
              </ul>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {isLoadingParticipant ? (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 animate-spin" />
                <span className="text-sm">Checking profile...</span>
              </div>
            ) : canStart ? (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span className="text-sm text-green-600">Ready to participate</span>
              </div>
            ) : (
              <span className="text-sm text-yellow-600">Profile setup required</span>
            )}
          </div>
          
          <Button onClick={onStart} className="gap-2" size="lg">
            {needsProfile ? 'Complete Setup & Start' : 'Begin New Session'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}